import { requestServices } from "@/utils/request";

/**
 * One promocode of a sold gift-card / membership-voucher ticket, resolved per
 * ticket token (ORD-659).
 */
export interface IVoucherPrintCodeEntry {
  /** Unique ticket token id (`OrderToken.id`) — token values can repeat across
   * tickets (one-token vouchers), so match tickets by this id. */
  tokenId: string;
  /** Ticket token the promocode belongs to */
  token: string;
  code: string | null;
  /** Unique promocode — the preferred display code */
  codeUnique: string | null;
  /** Validity start the printed ticket must display. The backend resolves the
   * campaign display logic (day-pass overrides, gift-card validity) here. */
  ticketStartDateTime: string | null;
  /** Validity end the printed ticket must display */
  ticketEndDateTime: string | null;
}

export interface IVoucherPrintCodeLine {
  /** Order line uid */
  lineId: string;
  /** Order line item type (internal enum: GIFT_CARD=2, MEMBERSHIP_VOUCHER=16) */
  itemType: number;
  /** Number of active ticket tokens on the line */
  expectedCount: number;
  /** True when every ticket of the line has its promocode synced */
  synced: boolean;
  vouchers: IVoucherPrintCodeEntry[];
}

export interface IVoucherPrintCodesResponse {
  orderId: string;
  /** True when all gift-card/membership-voucher lines are fully synced. False
   * means the async backend sync has not created every promocode yet — poll
   * again. */
  complete: boolean;
  lines: IVoucherPrintCodeLine[];
}

/**
 * Get the promocodes of an order's sold gift-card / membership-voucher tickets,
 * resolved per ticket token, for POS ticket printing.
 * `GET /v1/internal/orders/:orderId/voucher-codes`
 *
 * @param orderId Order id
 */
export async function getOrderVoucherPrintCodes(
  orderId: string
): Promise<IVoucherPrintCodesResponse> {
  const response = await requestServices({
    method: "GET",
    url: `/v1/internal/orders/${orderId}/voucher-codes`,
  });
  return response.data.data;
}
